import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "wmm-session-token";

/**
 * Apply (or clear) the session token everywhere it is needed:
 *  - localStorage, so it survives reloads
 *  - axios default Authorization header, so EVERY API call carries it
 *
 * Why a bearer token on top of the httpOnly cookie? When users arrive via the
 * WMM SSO handoff, some browsers treat our cookie as cross-site and silently
 * drop it - the exchange succeeds but every next API call 401s (symptom: the
 * sites dropdown goes empty). The bearer header works in every context.
 */
const applyToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem(TOKEN_KEY);
    delete axios.defaults.headers.common.Authorization;
  }
};

// Bootstrap at module load - before any component fires a request.
applyToken(localStorage.getItem(TOKEN_KEY) || null);

/** Append the session token to a plain-navigation URL (PDF/zip downloads,
 * window.open targets) which cannot carry an Authorization header. */
export const withSession = (url) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (!t) return url;
  return `${url}${url.includes("?") ? "&" : "?"}session=${encodeURIComponent(t)}`;
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data.user || null);
    } catch (e) {
      if (e?.response?.status === 401) applyToken(null); // stale token - drop it
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Emergency fallback: hardcoded operator credentials.
  const loginLocal = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    applyToken(data.session_token || null);
    setUser(data.user);
    return data.user;
  }, []);

  // WMM SSO handoff: exchange the one-shot token for a local session.
  const ssoExchange = useCallback(async (token) => {
    const { data } = await axios.post(`${API}/auth/sso`, { token });
    applyToken(data.session_token || null);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      /* session may already be gone - clear locally regardless */
    }
    applyToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, loginLocal, ssoExchange, logout, refresh }),
    [user, loading, loginLocal, ssoExchange, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
